/**
 * An utility function to make all the methods in a class hard bounded to the instance.
 * this way, you can call it from anywhere, and it still has no issues with 'this'
 * @param self the class that needs binding
 */
export const hardBind = (self: any) => {
	for (const key of Object.getOwnPropertyNames(self.constructor.prototype)) {
		const val = self[key];

		if (key !== 'constructor' && typeof val === 'function') {
			self[key] = val.bind(self);
		}
	}

	return self;
};

/**
 * An decorater that enables you to bind your method to a class (this.methodName = this.methodName.bind(this)),
 * which is needed if your method is called from somewhere else, and needs access to the class itself.
 * If your method never uses 'this' this is not needed at all.
 */
export function bind<T extends Function>(
	target: object,
	propertyKey: string,
	descriptor: TypedPropertyDescriptor<T>
): TypedPropertyDescriptor<T> | void {
	if (!descriptor || typeof descriptor.value !== 'function') {
		throw new TypeError(`Only methods can be decorated with @bind. <${propertyKey}> is not a method!`);
	}

	return {
		configurable: true,
		get(this: T): T {
			const bound: T = descriptor.value.bind(this);
			Object.defineProperty(this, propertyKey, {
				value: bound,
				configurable: true,
				writable: true,
			});
			return bound;
		},
	};
}
